// src/components/charts/TrendChart.tsx
//
// ★★★ WAVE 27 — P2C1 §12.83(k) *"Charting. For example, pie/column/line charts"*.
// The product had a column chart (`StatusBarChart`) and no line chart at all.
//
// ══ THE FORM WAS CHOSEN BEFORE THE COLOUR, AND THE COLOUR WAS NOT CHOSEN AT ALL ══
//
// Following the `dataviz` procedure rather than taste:
//
//  1. **The data's job is CHANGE OVER TIME** — how many bills carry each service
//     date across a window. `choosing-a-form.md` puts an ordered, continuous,
//     evenly-spaced axis on a line, not a bar: thirty bars invite a reader to
//     compare day 3 with day 27, which is not the question, and a line makes the
//     shape of the month the subject.
//  2. **Colour is taken, not picked.** Every series uses `--pt-series-N` in slot
//     order — the eight-slot categorical palette `ux-direction.md` §5.2 already
//     validated on both plot surfaces, with the validator output published beside
//     it. Nothing here invents a hue, and nothing cycles: a ninth series is not
//     representable by this component, by design.
//  3. **One axis.** §5.3 rule 4, and `anti-patterns.md`'s number-one entry. Bills
//     per day and decisions per day are both counts of the same kind, so they can
//     share a scale; anything that could not would be a second chart.
//
// ══ ⚠ WHAT THIS COMPONENT REFUSES TO DO ══
//
// * **It will not draw without stating its period.** `subtitle` is required, and
//   the caller builds it from the SERVER's window object. §5.3 rule 10 — *every
//   chart states its period basis* — and on this estate that rule is load-bearing
//   rather than ceremonial: the seeded corpus sits weeks ahead of the machine's
//   clock, so a chart implying "the last 30 days" would be false on every screen.
// * **It will not hide its numbers.** The table view is not an option a caller may
//   omit (§5.3 rule 2): it is the accessibility fallback, the relief for the three
//   light-surface palette slots that sit below 3:1, the answer to "what was the
//   exact figure", and what C-16 prints.
// * **It will not scale to its own minimum.** The y-axis starts at zero. Counts
//   have a real floor and a truncated one exaggerates every wobble.
import { useId, useMemo, useRef, useState, type ReactElement } from 'react'
import { SortableTh, SortAnnouncement } from '../SortableTh'
import { useClientSort, type ClientSortColumns } from '../../lib/useClientSort'

export interface TrendSeries {
  /** Rendered in the legend, the table header and the tooltip. */
  readonly label: string
  /** 1-8 — a slot in the validated categorical palette, never a hex value. */
  readonly slot: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  readonly points: readonly { readonly date: string; readonly value: number }[]
}

const PLOT_WIDTH = 720
const PLOT_HEIGHT = 200
const PAD_LEFT = 44
const PAD_BOTTOM = 26
const PAD_TOP = 10
const PAD_RIGHT = 12

/** A y-axis that ends on a round number rather than on the tallest bar. */
function niceCeiling(max: number): number {
  if (max <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(max))
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = magnitude * step
    if (candidate >= max) return candidate
  }
  return magnitude * 10
}

export function TrendChart({
  title,
  subtitle,
  series,
  dateLabel,
}: {
  readonly title: string
  /** REQUIRED. The period basis, in the server's words. See this file's header. */
  readonly subtitle: string
  readonly series: readonly TrendSeries[]
  /** Formats a `YYYY-MM-DD` for a human — the caller owns date formatting. */
  readonly dateLabel: (iso: string) => string
}): ReactElement {
  const [showTable, setShowTable] = useState(false)
  const [hovered, setHovered] = useState<number | null>(null)
  const tableId = useId()

  /**
   * ★★★ WAVE 27 (owner-directed) — DRAG ACROSS THE PLOT TO ZOOM INTO A RANGE.
   *
   * ⚠ **IT NARROWS THE VIEW, IT DOES NOT RE-ASK THE SERVER.** Every point already
   * on screen came from one request that every caption on the page is built from;
   * fetching a narrower window on drag would let the chart cover a period the
   * heading beside it still described. The reader is choosing what to LOOK at
   * within the period they chose; the period control above the band is what
   * changes the period.
   *
   * ⚠ **AND THE SUBTITLE SAYS WHICH IT IS.** While a zoom is active the chart
   * prints the dates it is actually showing AND the fact that it is a zoom, so the
   * §5.3 rule 10 basis stays true rather than becoming a claim about a window the
   * plot no longer draws.
   *
   * ⚠ A drag shorter than two points is treated as a CLICK, not a zoom —
   * otherwise every stray press on the plot collapses the chart to one day and
   * looks broken.
   */
  const [zoom, setZoom] = useState<{ readonly from: number; readonly to: number } | null>(null)
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [expanded, setExpanded] = useState(false)
  const svgRef = useRef<SVGSVGElement | null>(null)

  const allDates = series[0]?.points.map((p) => p.date) ?? []
  /*
   * ⚠ THE SLICE IS APPLIED ONCE, HERE, and everything below — the axis, the
   * marks, the hover layer and the TABLE — reads from it. A zoom that redrew the
   * plot while the table still listed the whole window would be two answers to one
   * question, which is the defect the table view exists to prevent.
   */
  const visible =
    zoom === null
      ? series
      : series.map((one) => ({ ...one, points: one.points.slice(zoom.from, zoom.to + 1) }))
  const dates = zoom === null ? allDates : allDates.slice(zoom.from, zoom.to + 1)
  const max = useMemo(
    () => visible.reduce((h, s) => s.points.reduce((hh, p) => Math.max(hh, p.value), h), 0),
    [visible],
  )
  const ceiling = niceCeiling(max)
  const innerWidth = PLOT_WIDTH - PAD_LEFT - PAD_RIGHT
  const innerHeight = PLOT_HEIGHT - PAD_TOP - PAD_BOTTOM
  const step = dates.length > 1 ? innerWidth / (dates.length - 1) : 0
  const xOf = (index: number): number => PAD_LEFT + index * step
  const yOf = (value: number): number => PAD_TOP + innerHeight - (value / ceiling) * innerHeight

  // Four gridlines including the ceiling and the floor. Recessive, per §5.3 rule 6.
  // ⚠ DEDUPED: on a small ceiling (1 or 2) rounding collapses several quarters onto the same integer (0, 0, 1, 1, 1), which
  // repeated a gridline and its label on top of itself and gave React duplicate keys.
  const ticks = [...new Set([0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(ceiling * f)))]

  const empty = dates.length === 0

  /*
   * ★★★ WAVE 27 (owner-directed) — THE TABLE VIEW'S HEADERS SORT, like every other
   * data table in this product. A chart's table looks incidental — a fallback
   * behind a toggle — which is exactly why it gets missed and exactly why the rule
   * exists.
   *
   * ⚠ A CLIENT sort is honest here: these rows are the chart's complete plotted
   * set, not a page of a capped list, so reordering them in the browser reorders
   * the whole population. And it follows the ZOOM — sorting a zoomed chart's table
   * must not silently list days the plot is no longer drawing.
   *
   * ⚠ The value columns sort NUMERICALLY even though each cell renders as a
   * formatted string: "1,000" sorts before "9" as text, which is the commonest
   * defect a table sort ships with.
   */
  const tableRows = useMemo(
    (): Record<string, string | number>[] =>
      dates.map((date, index) => ({
        date,
        ...Object.fromEntries(visible.map((one) => [one.label, one.points[index]?.value ?? 0])),
      })),
    [dates, visible],
  )
  const tableColumns = useMemo(() => {
    const spec: Record<string, { type: 'text' | 'number'; valueOf: (row: Record<string, string | number>) => string | number | null }> = {
      // ⚠ Sorted on the ISO string, which is why the SERVER's `YYYY-MM-DD` is kept
      // on the row beside the formatted label: "1 Nov" sorts before "15 Oct" as
      // text, and a date column that orders wrongly is worse than one that does
      // not sort at all.
      date: { type: 'text', valueOf: (row) => String(row['date'] ?? '') },
    }
    for (const one of visible) {
      spec[one.label] = { type: 'number', valueOf: (row) => Number(row[one.label] ?? 0) }
    }
    return spec as ClientSortColumns<Record<string, string | number>, string>
  }, [visible])
  const { rows: orderedRows, sort, onSort, interacted } = useClientSort(tableRows, tableColumns)
  const columnLabels = useMemo(
    () => ({ date: 'Date', ...Object.fromEntries(visible.map((one) => [one.label, one.label])) }),
    [visible],
  )

  return (
    <figure className="pt-chart-figure">
      <figcaption>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg">{title}</h3>
            {/*
              ⚠ The period basis, always. It is a `<p>` and not a `title` attribute
              because a basis nobody can read without hovering is not stated.
            */}
            <p className="text-sm text-[var(--sgds-color-subtle)]">
              {subtitle}
              {zoom !== null && dates.length > 0 && (
                <>
                  {' '}
                  <strong>
                    Zoomed to {dateLabel(dates[0] ?? '')} – {dateLabel(dates[dates.length - 1] ?? '')} (
                    {dates.length} of {allDates.length} days).
                  </strong>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {zoom !== null && (
              <button type="button" className="pt-linkish" onClick={() => setZoom(null)}>
                Reset zoom
              </button>
            )}
            {/*
              ⚠ EXPAND IS A HEIGHT CHANGE, not a modal. A dialog would trap focus,
              need its own dismissal and hide the page's other charts — and the thing
              a reader wants when a thirty-day plot is cramped is a taller plot, not
              a different screen.
            */}
            <button
              type="button"
              className="pt-linkish"
              aria-pressed={expanded}
              onClick={() => setExpanded((was) => !was)}
            >
              {expanded ? 'Shrink' : 'Expand'}
            </button>
            <button
              type="button"
              className="pt-linkish"
              aria-expanded={showTable}
              aria-controls={tableId}
              onClick={() => setShowTable((open) => !open)}
            >
              {showTable ? 'Hide the figures' : 'Show the figures'}
            </button>
          </div>
        </div>
      </figcaption>

      {empty ? (
        <p className="mt-4 text-[var(--sgds-body-color-subtle)]">
          No day in this window carries a value your scope can read.
        </p>
      ) : (
        <>
          {/*
            ⚠ A legend whenever there are two or more series (§5.3 rule 1). One
            series needs none — the title names it — and adding one anyway trains a
            reader to skip legends on the charts that do need them.
          */}
          {visible.length >= 2 && (
            <ul className="pt-chart-legend" aria-hidden="true">
              {visible.map((s) => (
                <li key={s.label}>
                  <span className={`pt-swatch pt-series-${s.slot}`} />
                  {s.label}
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-[var(--sgds-color-subtle)] mt-2">
            Drag across the plot to zoom into a range. The figures below follow the zoom.
          </p>
          <div className="pt-chart-plot">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${PLOT_WIDTH} ${PLOT_HEIGHT}`}
              className={`w-full h-auto${expanded ? ' pt-chart-expanded' : ''}`}
              role="img"
              aria-label={`${title}. ${subtitle}. The figures are available as a table.`}
              onMouseLeave={() => {
                setHovered(null)
                setDragFrom(null)
              }}
              onMouseUp={() => {
                /*
                 * ⚠ A DRAG OF FEWER THAN TWO POINTS IS A CLICK, not a zoom. Without
                 * this every stray press on the plot collapses the chart to a single
                 * day, which reads as a rendering fault rather than as an action the
                 * reader took.
                 */
                if (dragFrom !== null && hovered !== null && Math.abs(hovered - dragFrom) >= 1) {
                  setZoom({ from: Math.min(dragFrom, hovered), to: Math.max(dragFrom, hovered) })
                }
                setDragFrom(null)
              }}
            >
              {ticks.map((tick) => (
                <g key={tick}>
                  <line
                    className="pt-chart-grid"
                    x1={PAD_LEFT}
                    x2={PLOT_WIDTH - PAD_RIGHT}
                    y1={yOf(tick)}
                    y2={yOf(tick)}
                  />
                  <text className="pt-chart-tick" x={PAD_LEFT - 8} y={yOf(tick) + 4} textAnchor="end">
                    {tick.toLocaleString('en-SG')}
                  </text>
                </g>
              ))}

              {visible.map((s) => (
                <polyline
                  key={s.label}
                  className={`pt-chart-line pt-series-stroke-${s.slot}`}
                  points={s.points.map((p, i) => `${xOf(i)},${yOf(p.value)}`).join(' ')}
                />
              ))}

              {/*
                ⚠ THE HOVER LAYER IS A COLUMN PER DAY, NOT A HIT TEST ON THE MARK.
                `interaction.md`: hit targets bigger than the mark. A 2px line is
                unhittable with a mouse and impossible with a trackpad, and a chart
                whose values are only reachable by precise pointing has no hover
                layer at all — it has a demonstration of one.
              */}
              {dates.map((date, index) => (
                <rect
                  key={date}
                  className="pt-chart-hit"
                  x={xOf(index) - step / 2}
                  y={PAD_TOP}
                  width={Math.max(step, 6)}
                  height={innerHeight}
                  onMouseEnter={() => setHovered(index)}
                  onMouseDown={() => setDragFrom(index)}
                />
              ))}

              {dragFrom !== null && hovered !== null && dragFrom !== hovered && (
                <rect
                  className="pt-chart-selection"
                  x={Math.min(xOf(dragFrom), xOf(hovered))}
                  y={PAD_TOP}
                  width={Math.abs(xOf(hovered) - xOf(dragFrom))}
                  height={innerHeight}
                />
              )}

              {hovered !== null && (
                <g>
                  <line
                    className="pt-chart-crosshair"
                    x1={xOf(hovered)}
                    x2={xOf(hovered)}
                    y1={PAD_TOP}
                    y2={PAD_TOP + innerHeight}
                  />
                  {visible.map((s) => {
                    const point = s.points[hovered]
                    return point === undefined ? null : (
                      <circle
                        key={s.label}
                        className={`pt-chart-marker pt-series-fill-${s.slot}`}
                        cx={xOf(hovered)}
                        cy={yOf(point.value)}
                        r={4}
                      />
                    )
                  })}
                </g>
              )}

              {/* First and last date only. A label on every day is unreadable at 30. */}
              <text className="pt-chart-tick" x={PAD_LEFT} y={PLOT_HEIGHT - 6}>
                {dateLabel(dates[0] ?? '')}
              </text>
              <text
                className="pt-chart-tick"
                x={PLOT_WIDTH - PAD_RIGHT}
                y={PLOT_HEIGHT - 6}
                textAnchor="end"
              >
                {dateLabel(dates[dates.length - 1] ?? '')}
              </text>
            </svg>

            {/*
              The tooltip is HTML rather than SVG text so it wraps, inherits type
              tokens, and is readable at any zoom. It never becomes the only route
              to a value — the table below is (§5.3 rule 7).
            */}
            {hovered !== null && (
              <div className="pt-chart-tooltip" role="status">
                <p className="font-semibold">{dateLabel(dates[hovered] ?? '')}</p>
                {visible.map((s) => (
                  <p key={s.label}>
                    <span className={`pt-swatch pt-series-${s.slot}`} />
                    {s.label}: <strong>{(s.points[hovered]?.value ?? 0).toLocaleString('en-SG')}</strong>
                  </p>
                ))}
              </div>
            )}
          </div>

          <div id={tableId} hidden={!showTable}>
            <SortAnnouncement
              sort={sort}
              interacted={interacted}
              busy={false}
              columnLabels={columnLabels}
              rowCount={orderedRows.length}
            />
            <div className="pt-table-wrap mt-4">
              <table className="pt-table">
                <caption className="sr-only">
                  {title}: every plotted value. {subtitle}
                </caption>
                <thead>
                  <tr>
                    <SortableTh label="Date" sortKey="date" sort={sort} onSort={onSort} />
                    {visible.map((one) => (
                      <SortableTh
                        key={one.label}
                        label={one.label}
                        sortKey={one.label}
                        sort={sort}
                        onSort={onSort}
                        align="right"
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderedRows.map((row) => (
                    <tr key={String(row['date'])}>
                      <td>{dateLabel(String(row['date']))}</td>
                      {visible.map((one) => (
                        <td key={one.label} className="pt-tabular text-right">
                          {Number(row[one.label] ?? 0).toLocaleString('en-SG')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </figure>
  )
}
